import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { Transaction } from '@tiptap/pm/state';
import { Eye, PanelRightOpen } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useActiveEditor } from '@/store/editorContext';
import { useInvestmentInspectorStore } from '@/store/investmentInspectorStore';
import {
  addProductsToPhase,
  investmentDestinations,
  investmentGroupForPage,
  selectWorkspacePosition,
  type InvestmentDestination,
} from '@/editor/quoteWorkspace';
import type { PickedProduct } from '@/components/products/productPickerSelection';
import ProductPickerPanel from '@/components/products/ProductPickerPanel';
import { Switch } from '@/components/ui/switch';
import TiptapQuoteEditor, { type TiptapQuoteEditorHandle } from './TiptapQuoteEditor';
import SideBySideInspector from './SideBySideInspector';
import InvestmentInspector from './InvestmentInspector';
import type { EditorViewMode } from './EditorViewTabs';
import type { QuoteCustomerOverviewProps } from './QuoteCustomerOverview';

type EditorProps = React.ComponentProps<typeof TiptapQuoteEditor>;

/**
 * Volgt `value`, maar wacht met een wijziging tot de muis of vinger is losgelaten.
 * Klikken op een knop in het investeringsoverzicht selecteert dat overzicht al bij
 * het indrukken; opent het paneel dan direct, dan schuift de knop onder de muis
 * weg en gaat de klik verloren.
 */
function useValueAfterPointerRelease<T>(value: T): T {
  const [settled, setSettled] = useState(value);
  const pressedRef = useRef(false);

  useEffect(() => {
    const down = () => {
      pressedRef.current = true;
    };
    const up = () => {
      pressedRef.current = false;
    };
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    return () => {
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
    };
  }, []);

  useEffect(() => {
    if (Object.is(value, settled)) return;
    if (!pressedRef.current) {
      setSettled(value);
      return;
    }
    let timer = 0;
    // De click volgt direct op pointerup; pas daarna mag de layout verschuiven.
    const release = () => {
      timer = window.setTimeout(() => setSettled(value), 0);
    };
    window.addEventListener('pointerup', release, { capture: true, once: true });
    window.addEventListener('pointercancel', release, { capture: true, once: true });
    return () => {
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
      window.clearTimeout(timer);
    };
  }, [value, settled]);

  return settled;
}
interface QuoteWorkspaceProps {
  editorRef: RefObject<TiptapQuoteEditorHandle>;
  editorProps: Omit<EditorProps, 'ref'>;
  sidebar: ReactNode;
  documentToolbar: ReactNode;
  fullscreen: boolean;
  mode: EditorViewMode;
  onViewChange: (mode: EditorViewMode) => void;
  aiPanel: ReactNode;
  customerContext: QuoteCustomerOverviewProps;
}

export default function QuoteWorkspace({
  editorRef,
  editorProps,
  sidebar,
  documentToolbar,
  fullscreen,
  mode,
  onViewChange,
  aiPanel,
  customerContext,
}: QuoteWorkspaceProps) {
  const { documentEditor: editor, productPickerTableId, setProductPickerTableId } = useActiveEditor();
  const [showSelection, setShowSelection] = useState(true);
  const [destinations, setDestinations] = useState<InvestmentDestination[]>([]);
  const split = mode === 'side-by-side' || !!productPickerTableId;
  const phaseSelected = useInvestmentInspectorStore((state) => !!state.phase);
  const investment = useValueAfterPointerRelease(
    phaseSelected ||
      (!!editor && !!investmentGroupForPage(editor.state.doc, editorProps.selectedBlockId ?? null))
  );
  // Het rechterpaneel gaat vanzelf open zodra de selectie naar het investeringsoverzicht
  // gaat. Daarna blijft het open tot het met de knoppen rechtsboven wordt gesloten.
  const [pane, setPane] = useState({ investment, open: investment });
  if (pane.investment !== investment) setPane({ investment, open: investment || pane.open });
  const paneOpen = !!productPickerTableId || pane.open;

  useEffect(() => {
    if (!editor) return;
    const sync = (event?: { transaction: Transaction }) => {
      if (!event || event.transaction.docChanged)
        setDestinations(investmentDestinations(editor.state.doc));
    };
    sync();
    editor.on('transaction', sync);
    return () => {
      editor.off('transaction', sync);
    };
  }, [editor]);

  // Escape wist de selectie. Klikken naast de offerte laat de selectie staan, zodat
  // het bewerkpaneel niet wegspringt.
  useEffect(() => {
    if (!split || !editor) return;
    const deselect = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          '[data-overview-sorting="true"], input, textarea, select, [role="combobox"], [role="listbox"]'
        )
      )
        return;
      selectWorkspacePosition(editor, 0);
      useInvestmentInspectorStore.setState({ group: null, phase: null });
    };
    document.addEventListener('keydown', deselect);
    return () => {
      document.removeEventListener('keydown', deselect);
    };
  }, [editor, split]);

  const addProducts = useCallback(
    (items: PickedProduct[]) => {
      if (!editor || !productPickerTableId) return;
      const ids = addProductsToPhase(editor, productPickerTableId, items);
      const destination = destinations.find((entry) => entry.tableId === productPickerTableId);
      setProductPickerTableId(null);
      toast.success(
        `${ids.length} product${ids.length === 1 ? '' : 'en'} toegevoegd aan ${destination?.title || 'de fase'}`
      );
      editorRef.current?.flushSync();
    },
    [destinations, editor, editorRef, productPickerTableId, setProductPickerTableId]
  );

  return (
    <>
      {!fullscreen && documentToolbar}
      <div className="workspace-main">
        {!fullscreen && sidebar}
        <div className="workspace-content" data-split={split ? 'true' : 'false'}>
          <div className="workspace-preview">
            {split && (
              <header className="workspace-preview-heading" data-workspace-controls>
                <h2>
                  <Eye size={16} />
                  Klantpreview{' '}
                  <span>
                    {editorProps.selectedBlockId
                      ? `· Pagina ${editorProps.blocks.findIndex((block) => block.id === editorProps.selectedBlockId) + 1} van ${editorProps.blocks.length}`
                      : `· ${editorProps.blocks.length} pagina’s`}
                  </span>
                </h2>
                <div className="workspace-preview-controls">
                  <label>
                    Selectie tonen{' '}
                    <Switch
                      checked={showSelection}
                      onCheckedChange={setShowSelection}
                      aria-label="Selectiekaders tonen"
                    />
                  </label>
                  {!paneOpen && (
                    <button
                      type="button"
                      className="workspace-pane-toggle"
                      onClick={() => setPane({ investment, open: true })}
                      title="Bewerkpaneel tonen"
                    >
                      <PanelRightOpen size={16} />
                      Bewerken
                    </button>
                  )}
                </div>
              </header>
            )}
            <TiptapQuoteEditor
              ref={editorRef}
              {...editorProps}
              sideBySide={split}
              showSelection={showSelection}
            />
            {split && (
              <p className="workspace-preview-caption">
                {productPickerTableId
                  ? 'Scroll door de offerte terwijl je producten kiest. Nieuwe producten verschijnen na toevoegen.'
                  : 'Bewerk tekst direct in de offerte. Klik op een product om rechts de waarden te wijzigen.'}
              </p>
            )}
            {!split && <InvestmentInspector />}
          </div>
          {split && paneOpen && (
            <aside className="workspace-right-pane">
              <ProductPickerPanel
                open={!!productPickerTableId}
                onClose={() => setProductPickerTableId(null)}
                destinations={destinations}
                destinationId={productPickerTableId ?? ''}
                onDestinationChange={setProductPickerTableId}
                onPick={addProducts}
              />
              {!productPickerTableId && (
                <SideBySideInspector
                  customerContext={customerContext}
                  selectedBlockId={editorProps.selectedBlockId ?? null}
                  onDocumentMode={() => onViewChange('document')}
                  onCollapse={() => setPane({ investment, open: false })}
                />
              )}
            </aside>
          )}
        </div>
        {aiPanel}
      </div>
    </>
  );
}
